import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Stack, Typography, Divider, TextField, CircularProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { PALETTE, ApprovalRow, studentName, criteriaHeadline } from "../types";
import MasteryProgression from "./MasteryProgression";

type Props = {
  row: ApprovalRow | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (notes: string) => void;
  submitting?: boolean;
};

const ApproveMasteryDialog: React.FC<Props> = ({ row, open, onClose, onConfirm, submitting }) => {
  const [notes, setNotes] = useState("");
  const reset = () => setNotes("");
  const close = () => { if (submitting) return; reset(); onClose(); };
  if (!row) return null;

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: PALETTE.ink }}>Approve Mastery Progression</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Row label="Student" value={studentName(row)} />
          <Row label="Program" value={row.programName || "—"} />
          <Row label="Target" value={row.targetName || `Target #${row.targetId}`} />
          <Divider sx={{ my: 0.5, borderColor: PALETTE.border }} />
          <Row label="Progression" value={<MasteryProgression row={row} compact />} />
          <Row label="Criteria" value={criteriaHeadline(row.criteria)} />
        </Stack>
        <DialogContentText sx={{ fontSize: 13, color: PALETTE.slate }}>
          This will approve the mastery decision and apply the configured workflow transition.
        </DialogContentText>
        <TextField
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          label="Supervisor note (optional)"
          placeholder="Add context that will be preserved in the audit trail"
          fullWidth multiline minRows={2}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={submitting} sx={{ textTransform: "none", fontWeight: 700, color: PALETTE.slate }}>Cancel</Button>
        <Button
          onClick={() => onConfirm(notes)}
          disabled={submitting}
          variant="contained"
          startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CheckCircleOutlineIcon />}
          sx={{ textTransform: "none", fontWeight: 800, bgcolor: PALETTE.approved.accent, color: "#fff", "&:hover": { bgcolor: PALETTE.approved.fg } }}
        >
          {submitting ? "Approving…" : "Approve & Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="baseline">
    <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: PALETTE.slate, minWidth: 100 }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: 13.5, fontWeight: 700, color: PALETTE.ink, flex: 1 }}>{value}</Typography>
  </Stack>
);

export default ApproveMasteryDialog;
